import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Activity,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  History,
  Palette,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getWhatsNewReleasesForRole,
  type WhatsNewIcon,
  type WhatsNewRelease,
  type WhatsNewStep,
} from '../../content/whatsNew';
import { UserReleaseAcknowledgementService } from '../../services/UserReleaseAcknowledgementService';

export const WHATS_NEW_OPEN_EVENT = 'revahub:open-whats-new';

interface SeenReleaseState {
  releaseIds: string[];
}

const iconByName: Record<WhatsNewIcon, LucideIcon> = {
  evolution: FileText,
  timeline: History,
  exam: Camera,
  vitals: Activity,
  assessment: ClipboardCheck,
  design: Palette,
};

const storageKeyForUser = (userId: string) => `revahub:whats-new:seen:${userId}`;

const readSeenReleaseIds = (userId: string): string[] => {
  try {
    const rawValue = window.localStorage.getItem(storageKeyForUser(userId));
    if (!rawValue) return [];
    const parsed = JSON.parse(rawValue) as Partial<SeenReleaseState>;
    return Array.isArray(parsed.releaseIds)
      ? parsed.releaseIds.filter((releaseId): releaseId is string => typeof releaseId === 'string')
      : [];
  } catch {
    return [];
  }
};

const cacheSeenReleaseIds = (userId: string, releaseIds: string[]) => {
  try {
    window.localStorage.setItem(
      storageKeyForUser(userId),
      JSON.stringify({ releaseIds: Array.from(new Set(releaseIds)) } satisfies SeenReleaseState),
    );
  } catch {
    // A experiência continua utilizável quando o navegador bloqueia armazenamento local.
  }
};

const markLocalReleaseAsSeen = (userId: string, releaseId: string) => {
  cacheSeenReleaseIds(userId, [...readSeenReleaseIds(userId), releaseId]);
};

export const openWhatsNew = () => {
  window.dispatchEvent(new Event(WHATS_NEW_OPEN_EVENT));
};

const WhatsNewArchive = ({
  releases,
  titleId,
  onClose,
  onSelect,
}: {
  releases: WhatsNewRelease[];
  titleId: string;
  onClose: () => void;
  onSelect: (release: WhatsNewRelease) => void;
}) => (
  <section
    role="dialog"
    aria-modal="true"
    aria-labelledby={titleId}
    className="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-[1.75rem] bg-white shadow-2xl"
  >
    <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-8 sm:py-7">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-teal-800">
          <Sparkles className="h-3.5 w-3.5" />
          O que há de novo
        </span>
        <h2 id={titleId} className="mt-3 text-2xl font-semibold text-slate-900 sm:text-3xl">
          Histórico de novidades
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Consulte todas as melhorias já apresentadas no RevaHub.
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        aria-label="Fechar novidades"
      >
        <X className="h-5 w-5" />
      </button>
    </header>

    <div className="min-h-0 space-y-3 overflow-y-auto bg-slate-50/70 px-5 py-5 sm:px-8 sm:py-6">
      {releases.map((release, index) => (
        <button
          key={release.id}
          type="button"
          onClick={() => onSelect(release)}
          className="group flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-teal-200 hover:shadow-md sm:p-5"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-800 transition group-hover:bg-teal-100">
            <History className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              <span>{release.versionLabel}</span>
              <span aria-hidden="true">·</span>
              <span>{release.publishedAt}</span>
              {index === 0 && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 tracking-normal text-emerald-700">
                  Mais recente
                </span>
              )}
            </span>
            <span className="mt-1.5 block text-base font-semibold text-slate-900 sm:text-lg">
              {release.title}
            </span>
            <span className="mt-1 block text-sm leading-5 text-slate-600">
              {release.introduction}
            </span>
            <span className="mt-2 block text-xs font-medium text-teal-800">
              {release.steps.length} {release.steps.length === 1 ? 'novidade' : 'novidades'} nesta atualização
            </span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-teal-700" />
        </button>
      ))}
    </div>
  </section>
);

const StepVisual = ({ step, stepNumber }: { step: WhatsNewStep; stepNumber: number }) => {
  const Icon = iconByName[step.icon];

  if (step.media) {
    return (
      <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
        <a
          href={step.media.src}
          target="_blank"
          rel="noreferrer"
          aria-label={`Ampliar imagem: ${step.media.alt}`}
          className="group relative block bg-slate-100"
        >
          <img
            src={step.media.src}
            alt={step.media.alt}
            className="h-52 w-full object-cover transition group-hover:brightness-95 sm:h-64"
          />
          <span className="absolute bottom-3 right-3 rounded-full bg-slate-950/75 px-3 py-1.5 text-xs font-semibold text-white shadow-sm backdrop-blur">
            Ampliar imagem
          </span>
        </a>
        {step.media.caption && (
          <figcaption className="border-t border-slate-200 px-4 py-2 text-xs text-slate-500">
            {step.media.caption}
          </figcaption>
        )}
      </figure>
    );
  }

  return (
    <div className="relative h-48 overflow-hidden rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 via-white to-sky-50 sm:h-56">
      <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-teal-100/70" />
      <div className="absolute -bottom-16 left-10 h-40 w-40 rounded-full bg-sky-100/70" />
      <div className="relative flex h-full items-center justify-center">
        <div className="w-[78%] rounded-2xl border border-white/90 bg-white/90 p-4 shadow-[0_18px_50px_rgba(15,118,110,0.12)] backdrop-blur sm:p-5">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-100 text-teal-800">
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-2.5 w-2/3 rounded-full bg-slate-700" />
              <div className="h-2 w-5/6 rounded-full bg-slate-200" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="h-9 rounded-lg bg-teal-50" />
            <div className="h-9 rounded-lg bg-slate-100" />
            <div className="h-9 rounded-lg bg-sky-50" />
          </div>
        </div>
        <span className="absolute right-4 top-4 rounded-full border border-white bg-white/80 px-2.5 py-1 text-xs font-semibold text-teal-800 shadow-sm">
          {String(stepNumber).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

const WhatsNewExperience = () => {
  const { user, selectedClient, isAuthenticated, isLoading } = useAuth();
  const titleId = useId();
  const [activeRelease, setActiveRelease] = useState<WhatsNewRelease | null>(null);
  const [releaseQueue, setReleaseQueue] = useState<WhatsNewRelease[]>([]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const role = selectedClient?.userRole || user?.userGlobalRole || user?.userRole;
  const eligibleReleases = useMemo(() => getWhatsNewReleasesForRole(role), [role]);
  const activeStep = activeRelease?.steps[activeStepIndex] || null;

  const showRelease = useCallback((release: WhatsNewRelease, queue: WhatsNewRelease[] = [release]) => {
    setReleaseQueue(queue);
    setActiveRelease(release);
    setActiveStepIndex(0);
    setIsArchiveOpen(false);
    setIsOpen(true);
    if (user?.userId) {
      UserReleaseAcknowledgementService.markViewed(user.userId, release.id).catch((error) => {
        console.warn('[WhatsNew] Não foi possível registrar a visualização no Supabase:', error);
      });
    }
  }, [user?.userId]);

  const showArchive = useCallback(() => {
    setReleaseQueue([]);
    setActiveRelease(null);
    setActiveStepIndex(0);
    setIsArchiveOpen(true);
    setIsOpen(true);
  }, []);

  const closeAndRemember = useCallback((continueToNextRelease = false) => {
    if (user?.userId && activeRelease) {
      markLocalReleaseAsSeen(user.userId, activeRelease.id);
      UserReleaseAcknowledgementService.markCompleted(user.userId, activeRelease.id).catch((error) => {
        console.warn('[WhatsNew] A conclusão será sincronizada no próximo acesso:', error);
      });
    }

    const activeQueueIndex = activeRelease
      ? releaseQueue.findIndex((release) => release.id === activeRelease.id)
      : -1;
    const remainingReleases = activeQueueIndex >= 0 ? releaseQueue.slice(activeQueueIndex + 1) : [];
    if (continueToNextRelease && remainingReleases.length > 0) {
      showRelease(remainingReleases[0], remainingReleases);
      return;
    }

    setReleaseQueue([]);
    setActiveRelease(null);
    setIsArchiveOpen(false);
    setIsOpen(false);
  }, [activeRelease, releaseQueue, showRelease, user?.userId]);

  useEffect(() => {
    if (!isAuthenticated) setIsOpen(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.userId || eligibleReleases.length === 0) return;
    if (!selectedClient?.clientId && user.userGlobalRole !== 'superAdmin') return;

    let cancelled = false;
    let timeoutId: number | undefined;
    const userId = user.userId;

    const loadAcknowledgements = async () => {
      const localReleaseIds = readSeenReleaseIds(userId);
      let completedReleaseIds = localReleaseIds;

      try {
        const acknowledgements = await UserReleaseAcknowledgementService.listForUser(userId);
        const serverCompletedIds = acknowledgements
          .filter((acknowledgement) => Boolean(acknowledgement.completedAt))
          .map((acknowledgement) => acknowledgement.releaseId);
        completedReleaseIds = Array.from(new Set([...serverCompletedIds, ...localReleaseIds]));
        cacheSeenReleaseIds(userId, completedReleaseIds);

        const localOnlyIds = localReleaseIds.filter(
          (releaseId) => !serverCompletedIds.includes(releaseId),
        );
        if (localOnlyIds.length > 0) {
          void Promise.allSettled(
            localOnlyIds.map((releaseId) =>
              UserReleaseAcknowledgementService.markCompleted(userId, releaseId),
            ),
          );
        }
      } catch (error) {
        console.warn('[WhatsNew] Usando confirmações locais temporariamente:', error);
      }

      if (cancelled) return;
      const unseenReleases = eligibleReleases
        .filter((release) => !completedReleaseIds.includes(release.id))
        .sort((first, second) => first.id.localeCompare(second.id));
      if (unseenReleases.length === 0) return;

      timeoutId = window.setTimeout(() => {
        if (!cancelled) showRelease(unseenReleases[0], unseenReleases);
      }, 700);
    };

    void loadAcknowledgements();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [
    eligibleReleases,
    isAuthenticated,
    isLoading,
    selectedClient?.clientId,
    showRelease,
    user?.userGlobalRole,
    user?.userId,
  ]);

  useEffect(() => {
    const handleManualOpen = () => {
      if (eligibleReleases.length > 0) showArchive();
    };
    window.addEventListener(WHATS_NEW_OPEN_EVENT, handleManualOpen);
    return () => window.removeEventListener(WHATS_NEW_OPEN_EVENT, handleManualOpen);
  }, [eligibleReleases, showArchive]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAndRemember(false);
      if (event.key === 'ArrowRight' && !isArchiveOpen && activeRelease) {
        setActiveStepIndex((current) => Math.min(current + 1, activeRelease.steps.length - 1));
      }
      if (event.key === 'ArrowLeft' && !isArchiveOpen) {
        setActiveStepIndex((current) => Math.max(current - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeRelease, closeAndRemember, isArchiveOpen, isOpen]);

  if (!isOpen) return null;

  if (isArchiveOpen) {
    return (
      <div
        className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm sm:p-6"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeAndRemember(false);
        }}
      >
        <WhatsNewArchive
          releases={eligibleReleases}
          titleId={titleId}
          onClose={() => closeAndRemember(false)}
          onSelect={(release) => showRelease(release, [release])}
        />
      </div>
    );
  }

  if (!activeRelease || !activeStep) return null;

  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === activeRelease.steps.length - 1;
  const activeQueueIndex = releaseQueue.findIndex((release) => release.id === activeRelease.id);
  const hasNextRelease = activeQueueIndex >= 0 && activeQueueIndex < releaseQueue.length - 1;
  const StepIcon = iconByName[activeStep.icon];

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeAndRemember(false);
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="grid max-h-[94vh] w-full max-w-5xl grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-[1.75rem] bg-white shadow-2xl lg:grid-cols-[0.35fr_0.65fr] lg:grid-rows-1"
      >
        <aside className="relative overflow-hidden bg-[#103b4a] px-5 py-5 text-white sm:px-7 sm:py-7 lg:min-h-[650px]">
          <div className="absolute -left-20 bottom-5 h-56 w-56 rounded-full border-[38px] border-teal-300/10" />
          <div className="absolute -right-10 top-12 h-32 w-32 rounded-full bg-teal-300/10 blur-2xl" />
          <div className="relative flex h-full flex-col">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-teal-100">
                <Sparkles className="h-3.5 w-3.5" />
                O que há de novo
              </span>
              <button
                type="button"
                onClick={() => closeAndRemember(false)}
                className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white lg:hidden"
                aria-label="Fechar novidades"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 lg:mt-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-200">
                {activeRelease.versionLabel} · {activeRelease.publishedAt}
              </p>
              <h2 id={titleId} className="mt-3 text-2xl font-semibold leading-tight sm:text-3xl">
                {activeRelease.title}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">{activeRelease.introduction}</p>
              <button
                type="button"
                onClick={showArchive}
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <History className="h-4 w-4" />
                Todas as atualizações
              </button>
            </div>

            <div className="mt-5 lg:mt-auto">
              <div className="mb-3 flex items-center justify-between text-xs font-medium text-slate-300">
                <span>Passo {activeStepIndex + 1} de {activeRelease.steps.length}</span>
                <span>{Math.round(((activeStepIndex + 1) / activeRelease.steps.length) * 100)}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-teal-300 transition-[width] duration-300"
                  style={{ width: `${((activeStepIndex + 1) / activeRelease.steps.length) * 100}%` }}
                />
              </div>
              <div className="mt-4 hidden flex-wrap gap-2 lg:flex">
                {activeRelease.steps.map((step, index) => (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setActiveStepIndex(index)}
                    className={`h-2.5 rounded-full transition-all ${
                      index === activeStepIndex ? 'w-8 bg-teal-300' : 'w-2.5 bg-white/25 hover:bg-white/45'
                    }`}
                    aria-label={`Abrir passo ${index + 1}: ${step.title}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="flex min-h-0 flex-col overflow-y-auto px-5 py-5 sm:px-8 sm:py-7 lg:px-10">
          <div className="hidden justify-end lg:flex">
            <button
              type="button"
              onClick={() => closeAndRemember(false)}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Fechar novidades"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="lg:-mt-2">
            <StepVisual step={activeStep} stepNumber={activeStepIndex + 1} />
            <div className="mt-5 flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-800">
                <StepIcon className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 sm:text-2xl">{activeStep.title}</h3>
                <p className="mt-1 text-sm font-medium text-teal-800">{activeStep.summary}</p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600 sm:text-base">{activeStep.description}</p>
            <ul className="mt-4 space-y-2.5">
              {activeStep.highlights.map((highlight) => (
                <li key={highlight} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="leading-5">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between lg:mt-auto">
            <button
              type="button"
              onClick={() => setActiveStepIndex((current) => Math.max(current - 1, 0))}
              disabled={isFirstStep}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </button>
            <button
              type="button"
              onClick={() => {
                if (isLastStep) closeAndRemember(true);
                else setActiveStepIndex((current) => current + 1);
              }}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800"
            >
              {isLastStep ? (hasNextRelease ? 'Ver próxima atualização' : 'Entendi as novidades') : 'Próximo'}
              {isLastStep && !hasNextRelease ? <Check className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatsNewExperience;
